import React, { createContext, useContext, useCallback, useEffect, useState } from "react"

const RecaptchaContext = createContext()

const RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY

if (!RECAPTCHA_SITE_KEY) {
  console.error("GATSBY_RECAPTCHA_SITE_KEY environment variable is required")
}

export const RecaptchaProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const isBrowser = typeof window !== "undefined"

  useEffect(() => {
    // Only run in browser environment
    if (!isBrowser) return

    // Check if script is already loaded
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      setIsLoaded(true)
      return
    }

    // Load the reCAPTCHA Enterprise script
    const script = document.createElement("script")
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`
    script.async = true
    script.defer = true
    
    script.onload = () => {
      setIsLoaded(true)
    }

    document.head.appendChild(script)

    return () => {
      // Cleanup script if component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [isBrowser])

  const executeRecaptcha = useCallback(async (action) => {
    // Return null during SSR
    if (!isBrowser) {
      return null
    }

    if (!isLoaded || !window.grecaptcha || !window.grecaptcha.enterprise) {
      console.warn("reCAPTCHA not loaded yet")
      return null
    }

    try {
      return new Promise((resolve) => {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action })
            resolve(token)
          } catch (error) {
            console.error("reCAPTCHA execution failed:", error)
            resolve(null)
          }
        })
      })
    } catch (error) {
      console.error("reCAPTCHA execution error:", error)
      return null
    }
  }, [isLoaded, isBrowser])

  const value = {
    executeRecaptcha,
    isLoaded: isBrowser ? isLoaded : false,
  }

  return (
    <RecaptchaContext.Provider value={value}>
      {children}
    </RecaptchaContext.Provider>
  )
}

export const useGoogleReCaptcha = () => {
  const context = useContext(RecaptchaContext)
  if (!context) {
    // During SSR, provide a fallback context
    if (typeof window === "undefined") {
      return {
        executeRecaptcha: async () => null,
        isLoaded: false,
      }
    }
    throw new Error("useGoogleReCaptcha must be used within a RecaptchaProvider")
  }
  return context
} 